import { io, type Socket } from 'socket.io-client';
import type {
  ServerToTvEvents,
  TvToServerEvents,
  TvJoinedPayload,
  PlayerConnectedPayload,
  PlayerDisconnectedPayload,
  GameFrameToTvPayload,
  GameStatePayload,
  WebRTCOfferPayload,
  WebRTCIceCandidatePayload,
  RoomGameSelectedPayload,
  RoomModeSelectedPayload,
  RoomGameStartPayload,
  RoomModeClearedPayload,
  RoomGameClearedPayload,
  RoomStageSelectedPayload,
  SetLocalePayload,
  NetworkCheckPayload,
  ConnectionQualityPayload,
  TvControlAck,
} from 'shared/lib/tv-game/types.ts';
import type { PoseKeypoint } from 'shared/lib/tv-game/pose.ts';
import type { IPoseConsumer } from '../ports/IPoseConsumer.ts';

type TvSocket = Socket<ServerToTvEvents, TvToServerEvents>;

const TV_CONTROL_TIMEOUT_MS = 5_000;

export class TvSocketAdapter {
  private readonly socket: TvSocket;
  private lastRoomCode: string | null = null;
  private readonly onConnectRejoin = (): void => {
    if (this.lastRoomCode) {
      this.socket.emit('tv:join', { code: this.lastRoomCode });
    }
  };

  constructor(private readonly poseConsumer: IPoseConsumer) {
    this.socket = io() as TvSocket;
    this.registerHandlers();
  }

  private registerHandlers(): void {
    // TEMP(WebRTC-only test): classic pose via Socket.IO — off; use WebRTC `onNativeGameFrame` path.
    // this.socket.on('pose:update', (payload: PoseUpdateToTvPayload) => {
    //   this.poseConsumer.onPoseUpdate(payload.playerId, payload.playerName, payload.keypoints);
    // });

    // Re-join the room after socket reconnects so tvSocketId stays fresh in the repo.
    // Without this, WebRTC offers from Swift are forwarded to the stale old socketId
    // and never reach the TV browser.
    this.socket.on('connect', this.onConnectRejoin);
  }

  dispose(): void {
    this.socket.off('connect', this.onConnectRejoin);
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.lastRoomCode = null;
  }

  joinRoom(code: string): void {
    this.lastRoomCode = code;
    this.socket.emit('tv:join', { code });
  }

  /** Fires on every socket.io connect (including reconnect). */
  onConnect(cb: () => void): void {
    this.socket.on('connect', cb);
  }

  /** Fires when socket transport is closed / interrupted. */
  onDisconnect(cb: (reason: string) => void): void {
    this.socket.on('disconnect', cb);
  }

  /** Current socket.io connected status. */
  isConnected(): boolean {
    return this.socket.connected;
  }

  /** Socket.IO id — session token for `POST /api/net/ip-report`. */
  getSocketId(): string | null {
    return this.socket.id ?? null;
  }

  sendConnectionQuality(payload: ConnectionQualityPayload): void {
    this.socket.emit('connection:quality', payload);
  }

  onNetworkCheck(cb: (p: NetworkCheckPayload) => void): void {
    this.socket.on('network:check', cb);
  }

  sendGameState(payload: GameStatePayload): void {
    this.socket.emit('game:state', payload);
  }

  // ── Headless room: the TV menu drives the game (ack from server; null — no answer in time) ─────────
  tvSelectGame(gameId: string): Promise<TvControlAck | null> {
    return new Promise((resolve) => {
      this.tvControl().emit('tv:select-game', { gameId }, (err, res) => resolve(err ? null : res));
    });
  }

  tvStartGame(gameId?: string): Promise<TvControlAck | null> {
    return new Promise((resolve) => {
      this.tvControl().emit('tv:start-game', gameId ? { gameId } : {}, (err, res) => resolve(err ? null : res));
    });
  }

  tvExitGame(): Promise<TvControlAck | null> {
    return new Promise((resolve) => {
      this.tvControl().emit('tv:exit-game', {}, (err, res) => resolve(err ? null : res));
    });
  }

  private tvControl() {
    return this.socket.timeout(TV_CONTROL_TIMEOUT_MS);
  }

  // ── WebRTC signaling ───────────────────────────────────────────────────────

  sendWebRTCAnswer(roomCode: string, playerId: string, sdp: RTCSessionDescriptionInit): void {
    this.socket.emit('webrtc:answer', { roomCode, playerId, sdp });
  }

  sendWebRTCIce(roomCode: string, playerId: string, candidate: RTCIceCandidateInit): void {
    this.socket.emit('webrtc:ice', { roomCode, playerId, candidate });
  }

  onGameFrame(cb: (p: GameFrameToTvPayload) => void): void {
    this.socket.on('game:frame', cb);
  }

  onWebRTCOffer(cb: (p: WebRTCOfferPayload) => void): void {
    this.socket.on('webrtc:offer', cb);
  }

  onWebRTCIce(cb: (p: WebRTCIceCandidatePayload) => void): void {
    this.socket.on('webrtc:ice', cb);
  }

  // ── Room lifecycle ─────────────────────────────────────────────────────────

  onTvJoined(cb: (p: TvJoinedPayload) => void): void {
    this.socket.on('tv:joined', cb);
  }

  onRoomGameSelected(cb: (p: RoomGameSelectedPayload) => void): void {
    this.socket.on('room:game-selected', cb);
  }

  onRoomStageSelected(cb: (p: RoomStageSelectedPayload) => void): void {
    this.socket.on('room:stage-selected', cb);
  }

  onRoomModeSelected(cb: (p: RoomModeSelectedPayload) => void): void {
    this.socket.on('room:mode-selected', cb);
  }

  onRoomGameStart(cb: (p: RoomGameStartPayload) => void): void {
    this.socket.on('room:game-start', cb);
  }

  onRoomModeCleared(cb: (p: RoomModeClearedPayload) => void): void {
    this.socket.on('room:mode-cleared', cb);
  }

  onRoomGameCleared(cb: (p: RoomGameClearedPayload) => void): void {
    this.socket.on('room:game-cleared', cb);
  }

  onPlayerConnected(cb: (p: PlayerConnectedPayload) => void): void {
    this.socket.on('player:connected', cb);
  }

  onPlayerDisconnected(cb: (p: PlayerDisconnectedPayload) => void): void {
    this.socket.on('player:disconnected', cb);
  }

  onLocaleChanged(cb: (p: SetLocalePayload) => void): void {
    this.socket.on('tv:locale-changed', cb);
  }
}

// Inline pose consumer bridge — allows WebRTC manager to also feed the same consumer
export class PoseConsumerBridge implements IPoseConsumer {
  private readonly sinks: IPoseConsumer[] = [];

  add(consumer: IPoseConsumer): void {
    this.sinks.push(consumer);
  }

  onPoseUpdate(playerId: string, playerName: string, keypoints: readonly PoseKeypoint[]): void {
    this.sinks.forEach((s) => s.onPoseUpdate(playerId, playerName, keypoints));
  }
}
